import { useMediaQuery } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { orderBy } from 'lodash'
import dynamic from 'next/dynamic'
import React, { useContext, useMemo } from 'react'
import { MenuProvided } from 'react-instantsearch-core'
import { connectMenu } from 'react-instantsearch-dom'

import { useAnalytics } from '../../../lib/analytics'
import { Actions, Categories } from '../../../lib/analytics/events'
import { Theme } from '../../../lib/theming/Theme'
import isoDateToDayOfMonth from '../../../lib/utils/isoDateToDayOfMonth'
import { TimezoneContext } from '../../timezone/TimezoneProvider'
import { Body2 } from '../../utils/typography'
import { ButtonItemConfig } from './ButtonItemConfig'
import RefinementNavBar from './RefinementNavBar'

const Switcher = dynamic(() => import('../../switcher/Switcher'))

export const useAlgoliaMenuItemsToNavBarItemsOrdered = ({
  algoliaItems,
  refine,
}: {
  algoliaItems: MenuProvided['items']
  refine: MenuProvided['refine']
}): ButtonItemConfig[] => {
  const { trackEvent } = useAnalytics()

  const orderedItems = useMemo(() => {
    const items = algoliaItems.map(item => ({
      active: item.isRefined,
      id: item.label,
      label: isoDateToDayOfMonth(item.label),
      onClick: () => {
        trackEvent({
          action: Actions.ButtonClickFilter,
          category: Categories.Schedule,
          label: item.value,
          location: Categories.Schedule,
        })
        refine(item.value)
      },
    }))

    return orderBy(items, 'label', 'asc')
  }, [algoliaItems, refine, trackEvent])

  return orderedItems
}

const ConnectedRefinmentNavBar = connectMenu(({ items: algoliaItems, refine }) => {
  const { breakpoints } = useTheme<Theme>()
  const mdUp = useMediaQuery(breakpoints.up('md'))
  const items = useAlgoliaMenuItemsToNavBarItemsOrdered({ algoliaItems, refine })
  const { trackEvent } = useAnalytics()
  const {
    defaultActiveTimezone,
    timezone: defaultTimezone,
    userTimezone,
    handleActiveTimezoneChange,
  } = useContext(TimezoneContext)

  const settingsItem = useMemo(() => {
    const isLocalDefault = defaultActiveTimezone === 'local'
    return mdUp ? (
      <>
        <div style={{ marginRight: '.5rem' }}>
          <Body2 style={{ lineHeight: 1, textTransform: 'uppercase' }}>Time zone</Body2>
        </div>
        <Switcher
          left={isLocalDefault ? defaultTimezone : userTimezone}
          right={isLocalDefault ? userTimezone : defaultTimezone}
          onChange={e => {
            handleActiveTimezoneChange(e)
            trackEvent({
              action: 'Timezone toggle',
              category: Categories.Schedule,
              label: e,
              location: Categories.Schedule,
            })
          }}
        />
      </>
    ) : (
      <></>
    )
  }, [
    defaultActiveTimezone,
    defaultTimezone,
    handleActiveTimezoneChange,
    mdUp,
    trackEvent,
    userTimezone,
  ])

  return <RefinementNavBar items={items} settingsItem={settingsItem} />
})

const DateRefinementNavBar = () => <ConnectedRefinmentNavBar attribute="date" />

export default DateRefinementNavBar
